import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/Api';
import { api_url_v1 } from '../utils/Config';

const initialState = {
  items: [],
  selectedEvent: null,
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  },
  loading: false,
  error: null
};

export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async ({ page, limit, activeFilters, search }, { getState }) => {
    const { auth: { token } } = getState();
    const processedParams = {};
    for (const key in activeFilters) {
      processedParams[key] = Array.isArray(activeFilters[key]) 
        ? activeFilters[key].join(',') 
        : activeFilters[key];
    }
    const params = {
      sort:'-created_at',
      page,
      limit,
      search,
      ...processedParams
    }
    const response = await api.get(`${api_url_v1}/events`, {
      params,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    
    return {
      data: response.data.data || [],
      meta: response.data.meta || {
        current_page: page,
        last_page: 1,
        per_page: limit,
        total: 0
      }
    };
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (eventData, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.post(`${api_url_v1}/events`, eventData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data' 
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Event creation failed');
    }
  }
);

export const fetchEventDetails = createAsyncThunk(
  'events/fetchEventDetails',
  async (id, { getState }) => {
    const { auth: { token } } = getState();
    const response = await api.get(`${api_url_v1}/events/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ id, data }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      const response = await api.post(`${api_url_v1}/events/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });
      return { id, updatedEvent: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Update failed');
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (id, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      await api.delete(`${api_url_v1}/events/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Delete failed');
    }
  }
);

const eventsSlice = createSlice({
  name: 'eventsSlice',
  initialState,
  reducers: {
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Events Cases
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch Event Details Cases
      .addCase(fetchEventDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEventDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEvent = action.payload.data;
      })
      .addCase(fetchEventDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Update Event Cases
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(event => event.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload.updatedEvent;
        }
        if (state.selectedEvent?.id === action.payload.id) {
          state.selectedEvent = action.payload.updatedEvent;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Update failed';
      })

      // Create Event Cases
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.meta.total = state.meta.total + 1;
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Event creation failed';
      })

      // Delete Event Cases
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(event => event.id !== action.payload);
        if (state.selectedEvent?.id === action.payload) {
          state.selectedEvent = null;
        }
        state.meta.total = Math.max(0, state.meta.total - 1);
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Delete failed';
      });
  }
});

export const { setSelectedEvent } = eventsSlice.actions;
export default eventsSlice.reducer;